import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  LogOut,
  Settings,
  ChevronDown,
  Mail,
  LayoutDashboard,
  Building2,
  Users,
  Target,
  Package,
  Key,
  Briefcase,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { AuthAnimation } from "@/components/auth/AuthAnimation";
import { companyAPI } from "@/services/companyService";
import { contactoAPI } from "@/services/contactoService";
import { opportunityAPI } from "@/services/opportunityService";

export function Header() {
  const { user, logout, company } = useAuth();
  const navigate = useNavigate();
  const [showLogoutAnimation, setShowLogoutAnimation] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const primaryColor = company?.primary_color || "#3b82f6";
  const secondaryColor = company?.secondary_color || "#8b5cf6";

  const handleLogout = () => {
    setShowLogoutAnimation(true);
  };

  const handleLogoutComplete = () => {
    logout();
  };

  const getUserInitials = () => {
    if (!user) return "US";

    if (user.first_name || user.last_name) {
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      const initials = `${firstName.charAt(0)}${lastName.charAt(
        0,
      )}`.toUpperCase();
      if (initials.trim()) return initials;
    }

    if (user.name) {
      return user.name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }

    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }

    return "US";
  };

  const getRoleDisplayName = () => {
    if (!user?.role?.name) return "Usuario";

    const roleMap: { [key: string]: string } = {
      "super admin": "Super Admin",
      admin: "Administrador",
      vendedor: "Vendedor",
      lector: "Lector",
      manager: "Gerente",
    };

    return roleMap[user.role.name.toLowerCase()] || user.role.name;
  };

  const getUserDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user?.name || "Usuario";
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      setShowResults(true);
      const results: any[] = [];
      const lowerQuery = searchQuery.toLowerCase();

      const modules = [
        {
          name: "Dashboard",
          href: "/dashboard",
          icon: LayoutDashboard,
          type: "Módulo",
        },
        {
          name: "Clientes",
          href: "/clients",
          icon: Briefcase,
          type: "Módulo",
        },
        {
          name: "Cotizaciones",
          href: "/opportunities",
          icon: Target,
          type: "Módulo",
        },
        {
          name: "Productos",
          href: "/products",
          icon: Package,
          type: "Módulo",
        },
        {
          name: "Empleados",
          href: "/employees",
          icon: Briefcase,
          type: "Módulo",
        },
        { name: "Usuarios", href: "/users", icon: Users, type: "Módulo" },
        { name: "Roles", href: "/roles", icon: Key, type: "Módulo" },
      ];

      const matchedModules = modules.filter((m) =>
        m.name.toLowerCase().includes(lowerQuery),
      );
      results.push(...matchedModules);

      if (company?.slug) {
        try {
          const [companiesRes, contactsRes, oppsRes] = await Promise.all([
            companyAPI
              .getCompanies({ slug: company.slug, limit: 100 })
              .catch(() => ({ data: [] })),
            contactoAPI.getContactos(company.slug).catch(() => []),
            opportunityAPI
              .getOpportunities({ slug: company.slug, limit: 100 })
              .catch(() => ({ data: [] })),
          ]);

          const matchedCompanies = (companiesRes.data || [])
            .filter((c: any) => c.name.toLowerCase().includes(lowerQuery))
            .slice(0, 3)
            .map((c: any) => ({
              name: c.name,
              href: `/companies?id=${c.id}`,
              icon: Building2,
              type: "Empresa",
            }));
          results.push(...matchedCompanies);

          const matchedContacts = (
            Array.isArray(contactsRes) ? contactsRes : []
          )
            .filter(
              (c: any) =>
                (c.name || `${c.firstName} ${c.lastName}`)
                  .toLowerCase()
                  .includes(lowerQuery) ||
                c.email?.toLowerCase().includes(lowerQuery),
            )
            .slice(0, 3)
            .map((c: any) => ({
              name: c.name || `${c.firstName} ${c.lastName}`,
              href: `/contacts`,
              icon: Users,
              type: "Contacto",
            }));
          results.push(...matchedContacts);

          const matchedOpps = (oppsRes.data || [])
            .filter((o: any) => o.name.toLowerCase().includes(lowerQuery))
            .slice(0, 3)
            .map((o: any) => ({
              name: o.name,
              href: `/opportunities`,
              icon: Target,
              type: "Oportunidad",
            }));
          results.push(...matchedOpps);
        } catch (err) {
          console.error("Error searching entities", err);
        }
      }

      setSearchResults(results);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, company?.slug]);

  const handleResultClick = (href: string) => {
    navigate(href);
    setShowResults(false);
    setSearchQuery("");
  };

  return (
    <>
      <style>{`
        :root {
          --header-primary: ${primaryColor};
          --header-secondary: ${secondaryColor};
        }
        .header-avatar-gradient {
          background: linear-gradient(135deg, var(--header-primary) 0%, var(--header-secondary) 100%);
          box-shadow: 0 10px 15px -3px rgba(var(--header-primary-rgb, 59, 130, 246), 0.3);
        }
        .header-logo-shadow {
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
        }
        .header-search-focus:focus {
          border-color: var(--header-primary) !important;
          ring-color: var(--header-primary) !important;
          box-shadow: 0 0 0 2px rgba(var(--header-primary-rgb, 59, 130, 246), 0.2);
        }
        .header-border-accent {
          border-bottom: 1px solid rgba(var(--header-primary-rgb, 59, 130, 246), 0.1);
        }
      `}</style>
      <header className="h-16 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/50 fixed top-4 left-6 right-6 md:left-10 md:right-10 z-50 rounded-2xl transition-all duration-300 ease-in-out border border-slate-800 header-border-accent">
        <div className="h-full px-6 flex items-center justify-between">
          <div
            className="flex items-center h-full cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => navigate("/dashboard")}
          >
            {company?.logo_url ? (
              <img
                src={company.logo_url}
                alt={company?.name || "Logo"}
                className="h-full w-auto max-h-full py-2 object-contain header-logo-shadow"
              />
            ) : (
              <div className="h-10 w-10 flex items-center justify-center bg-slate-800 rounded-lg header-logo-shadow">
                <Building2 className="h-6 w-6 text-slate-500" />
              </div>
            )}
            {company && (
                <div className="ml-3 hidden sm:flex flex-col border-l border-slate-700 pl-3">
                    <span className="text-white text-xs font-bold tracking-wider uppercase">{company.name}</span>
                    <span className="text-slate-500 text-[10px]">{company.nombre_legal || 'Organización'}</span>
                </div>
            )}

          </div>

          <div className="flex-1 max-w-xl mx-4 relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                name="global-search"
                id="global-search"
                autoComplete="off"
                placeholder="Buscar módulos, empresas, contactos..."
                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:bg-slate-800 header-search-focus"
                style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (searchQuery.trim()) setShowResults(true);
                }}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
              )}
            </div>

            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-50 max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="px-4 py-3 hover:bg-slate-800 cursor-pointer flex items-center gap-3 transition-colors"
                        onClick={() => handleResultClick(result.href)}
                      >
                        <div className="p-2 bg-slate-800 rounded-lg text-slate-400">
                          <result.icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {result.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {result.type}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {searchQuery.trim()
                      ? "No se encontraron resultados"
                      : "Escribe para buscar..."}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center space-x-3 hover:bg-slate-800 px-3 py-2 rounded-xl transition-all duration-200 group"
                >
                  <div className="relative">
                    <div 
                      className="h-10 w-10 shrink-0 aspect-square rounded-full flex items-center justify-center text-white font-semibold text-sm ring-1 ring-white/10 header-avatar-gradient"
                    >
                      {getUserInitials()}
                    </div>
                  </div>
                  <div className="hidden lg:flex flex-col items-start">
                    <span className="text-sm font-medium text-white">
                      {getUserDisplayName()}
                    </span>
                    <span className="text-xs text-slate-400">
                      {getRoleDisplayName()}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-white" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                className="w-60 border-slate-800 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/50"
                align="end"
              >
                <DropdownMenuLabel className="p-4 border-b border-slate-800">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div 
                        className="h-10 w-10 shrink-0 aspect-square rounded-full flex items-center justify-center text-white font-semibold text-sm ring-1 ring-white/10 header-avatar-gradient mt-0.5"
                      >
                        {getUserInitials()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white break-words whitespace-normal">
                          {getUserDisplayName()}
                        </p>
                        <p className="text-xs text-slate-400 flex items-start mt-1">
                          <Mail className="h-3 w-3 shrink-0 mr-2 mt-0.5" />
                          <span className="break-all whitespace-normal leading-relaxed">{user?.email || "email@ejemplo.com"}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator className="bg-slate-800" />

                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer text-slate-300 hover:text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white"
                >
                  <Settings className="h-4 w-4 mr-2 text-slate-400" />
                  <span>Configuración</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-slate-800" />

                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-red-900/20 focus:bg-red-900/20 focus:text-red-300"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Cerrar Sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <AuthAnimation
        isOpen={showLogoutAnimation}
        type="logout"
        onComplete={handleLogoutComplete}
      />
    </>
  );
}
